import React, { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { faMobile } from "@fortawesome/free-solid-svg-icons";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { QuickCreateLivestockSaleLotPanel } from "components/AgGrid/panels";
import { saleLotColumns } from "components/SaleLotsTable/columnDefs";

import { AgGridPanels, AgGridTables } from "constants/aggrid";
import { CommentTypes } from "constants/comments";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { SaleType } from "constants/sale";

import {
  getLivestockSaleId,
  getSaleRoute,
  getSaleyardName,
  openCommentsModal,
} from "lib/navigation";

import {
  currentSaleSelector,
  getActiveLivestockAgentDeployment,
  getCurrentSaleyard,
  getDentition,
  getSaleSubTypeById,
  selectGeneralSaleLotsAggridData,
  selectIsSaleSubTypeDentitionEnabled,
  selectVendorSplitByKeyLookup,
} from "selectors";

import {
  useDebounceSelector,
  useHasDeploymentPermission,
  useHasPermission,
  useIsMobile,
  useSetScreenSize,
} from "hooks";
import { ColDef } from "ag-grid-community";
import { EMPTY_OBJECT } from "lib";

const getRowId = (params: { data: SaleLot }) => params.data.id;

function P2PSaleLotsTableComponent(): JSX.Element {
  const rowData: SaleLot[] = useDebounceSelector(
    selectGeneralSaleLotsAggridData,
    500,
  );

  const deployment: Deployment = useSelector(getActiveLivestockAgentDeployment);

  const vendorSplitByKeyLookup: Record<string, VendorSplit> = useSelector(
    selectVendorSplitByKeyLookup,
  );

  const sale: Sale = useSelector(currentSaleSelector);
  const {
    date: saleDate,
    sale_title: saleTitle,
    sale_type: saleType,
    species_id: speciesId,
    sale_sub_type_id: saleSubTypeId,
  } = sale;

  const saleSubType =
    useSelector(getSaleSubTypeById(saleSubTypeId)) || EMPTY_OBJECT;

  const columnDefs: ColDef<SaleLot>[] = useMemo(
    () => saleLotColumns(saleType, null, null, speciesId, saleSubType),
    [saleType, saleSubType, speciesId],
  );

  const isMobile: boolean = useIsMobile();

  const handleClickSaleLotComments = ({ id }: SaleLot) =>
    openCommentsModal(CommentTypes.SALE_LOT, id, window.location.hash);

  const hasDentitionPermission: boolean = useHasDeploymentPermission(
    DeploymentPermissions.featureDentition,
  );
  const isDentitionEnabled: boolean = useSelector(
    selectIsSaleSubTypeDentitionEnabled,
  );
  const dentitionByIdLookup: Record<string, Dentition> =
    useSelector(getDentition);

  const P2PContext = useMemo(
    () => ({
      deployment,
      handleClickSaleLotComments,
      vendorSplitByKeyLookup,
      hasDentitionPermission,
      isDentitionEnabled,
      dentitionByIdLookup,
    }),
    [
      deployment,
      vendorSplitByKeyLookup,
      hasDentitionPermission,
      isDentitionEnabled,
      dentitionByIdLookup,
    ],
  );

  const allowLivestockQuickCreate: boolean = useHasPermission(
    getCurrentSaleyard,
    SaleyardPermissions.featureQuickCreateLivestock,
  );

  const history = useHistory();
  const { setMobile } = useSetScreenSize();

  const additionalActions = useMemo(
    () => [
      {
        title: "Mobile View",
        icon: faMobile,
        onClick: () => {
          setMobile();
          history.push(
            `${getSaleRoute(
              getSaleyardName(),
              getLivestockSaleId(),
            )}/auction-pen-cards`,
          );
        },
        dataTour: "mobileView",
      },
    ],
    [history, setMobile],
  );

  const panels = React.useMemo(
    () =>
      [
        allowLivestockQuickCreate && {
          id: AgGridPanels.QUICK_CREATE,
          labelDefault: "Quick Create",
          labelKey: "quickCreate",
          iconKey: "save",
          toolPanel: QuickCreateLivestockSaleLotPanel,
          width: !isMobile && 600,
        },
      ].filter(Boolean),
    [allowLivestockQuickCreate, isMobile],
  );

  return (
    <AgGridTable
      downloadFilename={`${saleDate}-${saleTitle}-SaleLotReport.csv`}
      columnDefs={columnDefs}
      getRowId={getRowId}
      context={P2PContext}
      rowData={rowData}
      tableName={AgGridTables.PRIVATE_SALE_SALELOT}
      panels={panels}
      defaultToolPanel={
        !isMobile && saleType !== SaleType.OVER_HOOKS
          ? AgGridPanels.QUICK_CREATE
          : null
      }
      additionalActions={additionalActions}
    />
  );
}

export default memo(P2PSaleLotsTableComponent);
